import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { cssColor } from '../badges';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';
import type { TicketStatus, TicketStatusDef } from '../../types';

interface Props {
  /** The company's status defs (inactive ones are filtered out here). */
  statuses: TicketStatusDef[];
  /** The ticket's current status key. */
  current: TicketStatus;
  /** Staged status in the compose variant — shown as "current → staged". */
  staged?: TicketStatus | null;
  onSelect: (key: TicketStatus) => void;
  /** Shown only when something is staged; clears it. */
  onClearStaged?: () => void;
  disabled?: boolean;
  /** `header` = standalone control that applies immediately;
   *  `compose` = smaller control that stages a change for the message. */
  variant?: 'header' | 'compose';
}

const MENU_WIDTH = 240;

function labelOf(statuses: TicketStatusDef[], key: string): string {
  return statuses.find(s => s.key === key)?.label ?? (key || '—').replace(/_/g, ' ');
}

function colorOf(statuses: TicketStatusDef[], key: string): string {
  return cssColor(statuses.find(s => s.key === key)?.color ?? 'muted');
}

/**
 * One-click status control: the trigger reads "Status: <label>" and opens the
 * full list on a single click — no intermediate "change status" step. Picking
 * a status that needs a resolution or closing reason is handed back to the
 * caller, which collects that text before applying.
 *
 * The menu is portalled to <body> because both mount points sit inside cards
 * with `overflow: hidden`, which would otherwise clip it.
 */
export default function StatusPicker({
  statuses,
  current,
  staged,
  onSelect,
  onClearStaged,
  disabled,
  variant = 'header',
}: Props) {
  const isCompose = variant === 'compose';
  const { open, setOpen, triggerRef, menuRef, style } = useAnchoredMenu({
    width: MENU_WIDTH,
    align: isCompose ? 'left' : 'right',
  });

  const options = statuses.filter(s => s.isActive || s.key === current);
  const selectedKey = staged ?? current;
  const currentCategory = statuses.find(s => s.key === current)?.category;

  // Mirrors the backend rule: resolving always needs a resolution, closing
  // needs a reason unless the ticket was resolved first. In the compose
  // variant the message body supplies it, so the hint would be noise.
  const needsNote = (def: TicketStatusDef) =>
    !isCompose && def.key !== current
    && (def.category === 'resolved' || (def.category === 'closed' && currentCategory !== 'resolved'));

  const pick = (key: string) => {
    setOpen(false);
    if (key !== selectedKey) onSelect(key);
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        className={isCompose ? 'btn btn-ghost text-xs py-1.5 gap-1.5' : 'btn btn-secondary gap-1.5'}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Change ticket status"
      >
        <span className="text-muted font-medium">Status:</span>
        <span style={{ color: colorOf(statuses, current), fontWeight: 700 }}>
          {labelOf(statuses, current)}
        </span>
        {staged && staged !== current && (
          <>
            <span className="text-muted">→</span>
            <span style={{ color: colorOf(statuses, staged), fontWeight: 700 }}>
              {labelOf(statuses, staged)}
            </span>
          </>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {staged && staged !== current && onClearStaged && (
        <button
          type="button"
          onClick={onClearStaged}
          className="ml-1 text-muted hover:text-err"
          title="Keep the current status"
          aria-label="Clear staged status change"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      {open && style && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          className="bg-surface border border-line rounded-lg shadow-xl overflow-y-auto"
          style={style}
        >
          <div className="py-1">
            {options.map(s => {
              const isSelected = s.key === selectedKey;
              return (
                <button
                  key={s.key}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => pick(s.key)}
                  className="w-full px-3 py-2 text-sm text-left hover:bg-row-odd flex items-center gap-2"
                >
                  <span className="w-4 flex items-center justify-center shrink-0">
                    {isSelected && <Check className="w-3.5 h-3.5 text-primary" />}
                  </span>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: cssColor(s.color) }} />
                  <span className="text-fg truncate">{s.label}</span>
                  {s.key === current && (
                    <span className="ml-auto text-[11px] text-muted shrink-0">current</span>
                  )}
                  {needsNote(s) && (
                    <span className="ml-auto text-[11px] text-muted shrink-0">needs a note</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
